import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { InviteModel } from '@core/models/invite.model';
import { ProjectModel } from '@core/models/project.model';
import { StatusModel } from '@core/models/status.model';
import * as AuthActions from '@core/redux/auth/auth.actions';
import * as FilterActions from '@core/redux/filter/filter.actions';
import * as fromRoot from '@core/redux/index';
import * as InviteActions from '@core/redux/invite/invite.actions';
import * as ProjectActions from '@core/redux/project/project.actions';
import * as StatusActions from '@core/redux/status/status.actions';
import * as TagActions from '@core/redux/tag/tag.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { ImageService } from '@core/services/image/image.service';
import { SocketService } from '@core/services/socket.service';
import { Store } from '@ngrx/store';
import { ImageComponent } from '@ux/components/image/image.component';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';
import { filter, map } from 'rxjs/operators';

import { HeaderComponent } from './components/header/header.component';
import { ProjectFormComponent } from './components/project-form/project-form.component';
import { ProjectMenuComponent } from './components/project-menu/project-menu.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SidebarFilterComponent } from './components/sidebar-filter/sidebar-filter.component';
import { SidebarUserComponent } from './components/sidebar-user/sidebar-user.component';

type IsPopupOpen = {
  isOpen: boolean;
  position: number;
};

type IsProjectFormOpen = {
  isOpen: boolean;
  left: number;
  top: number;
};

@Component({
  selector: 'app-cabinet',
  templateUrl: './cabinet.component.html',
  styleUrls: ['./cabinet.component.less'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ marginLeft: -220 }), animate('152ms ease-in', style({ marginLeft: 0 }))]),
      transition(':leave', [style({ marginLeft: 0 }), animate('152ms ease-out', style({ marginLeft: -220 }))]),
    ]),
  ],
  imports: [
    RouterModule,
    HeaderComponent,
    ImageComponent,
    SidebarComponent,
    SidebarFilterComponent,
    SidebarUserComponent,
    ProjectFormComponent,
    ProjectMenuComponent,
  ],
  standalone: true,
  providers: [SocketService, ImagePreviewPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CabinetComponent {
  private store = inject<Store<fromRoot.State>>(Store);
  private socket = inject(SocketService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  readonly imageService = inject(ImageService);

  // Сигналы для состояния
  readonly isShowSidebar = toSignal(this.store.select<boolean>(fromRoot.getShowSidebar), { initialValue: false });

  readonly isShowSidebarFilter = toSignal(this.store.select<boolean>(fromRoot.getShowSidebarFilter), {
    initialValue: false,
  });

  readonly popupProjectMenu = toSignal(this.store.select<IsPopupOpen>(fromRoot.getPopupProjectMenu), {
    initialValue: { isOpen: false, position: 0 },
  });

  readonly popupProjectForm = toSignal(this.store.select<IsProjectFormOpen>(fromRoot.getPopupProjectForm), {
    initialValue: { isOpen: false, top: 0, left: 0 },
  });

  readonly projectList = toSignal(this.store.select<ProjectModel[]>(fromRoot.getProjectActiveEntities), {
    initialValue: [],
  });

  readonly statusList = toSignal(this.store.select<StatusModel[]>(fromRoot.getStatusEntities), { initialValue: [] });

  readonly inviteList = toSignal(this.store.select<InviteModel[]>(fromRoot.getInviteActiveEntities), {
    initialValue: [],
  });

  readonly taskOpenId = toSignal(this.store.select<string>(fromRoot.getTaskSelectedId), { initialValue: null });

  readonly projectSelectedId = toSignal(this.store.select<string>(fromRoot.getProjectSelectedId), {
    initialValue: null,
  });

  constructor() {
    // Эффект для инициализации данных при изменении projectSelectedId
    effect(() => {
      const projectId = this.projectSelectedId();
      if (projectId) {
        setTimeout(() => {
          this._initData();
          this.socket.listenOnline(projectId);
        });
      }
    });

    this.store
      .select(fromRoot.getToken)
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        this.socket.listen(token);
      });

    // Подписка на изменение параметров маршрута
    this.route.params
      .pipe(
        map(params => params['projectId']),
        filter(projectId => !!projectId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((projectId: string) => {
        this.store.dispatch(new ProjectActions.SelectAction(projectId));
      });

    // Инициализация поиска проектов
    this.store.dispatch(new ProjectActions.SearchAction());
  }

  private _initData(): void {
    this.store.dispatch(new AuthActions.GetUserAction());
    this.store.dispatch(new FilterActions.SearchAction());
    this.store.dispatch(new TagActions.SearchAction());
    this.store.dispatch(new StatusActions.SearchAction());
    this.store.dispatch(new TaskActions.SearchFavoriteAction());
    this.store.dispatch(new TaskActions.SearchNotificationAction());
    this.store.dispatch(new InviteActions.SearchAction({}));
  }
}
